import { Button, Spinner } from '@consigli/facade';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { HiOutlineXMark } from 'react-icons/hi2';

import { isGenericSpeakerName } from './action-items-table-utils';
import { SOURCE_PANEL_CLASS, SOURCE_PANEL_SLIDE_MS } from './source-panel-slide';
import { formatMeetingDate, formatMeetingTime } from '../format-meeting-datetime';

import type { ParsedTranscriptEntry } from '../utils/export-types';

export interface TranscriptSourceMeeting {
  title?: string | null;
  scheduledStartTime?: string | null;
  scheduledEndTime?: string | null;
  location?: string | null;
}

interface TranscriptSourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  meeting?: TranscriptSourceMeeting;
  entries: ParsedTranscriptEntry[];
  segmentIds: string[];
  isLoading?: boolean;
  hasError?: boolean;
  onRetry?: () => void;
}

type ViewerProps = Omit<TranscriptSourceModalProps, 'isOpen'> & { isClosing?: boolean };

const TranscriptSourceViewer: FC<ViewerProps> = ({
  onClose,
  meeting,
  entries,
  segmentIds,
  isLoading,
  hasError,
  onRetry,
  isClosing = false,
}) => {
  const { t, i18n } = useTranslation();

  const [activeIndex, setActiveIndex] = useState(0);
  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const hasScrolledRef = useRef(false);

  const highlightedIds = useMemo(() => new Set(segmentIds), [segmentIds]);

  const matches = useMemo(
    () =>
      entries
        .map((entry) => entry.segmentId)
        .filter((id): id is string => !!id && highlightedIds.has(id)),
    [entries, highlightedIds],
  );

  const scrollToMatch = useCallback((segmentId: string | undefined, smooth: boolean) => {
    if (!segmentId) return;
    rowRefs.current.get(segmentId)?.scrollIntoView({
      behavior: smooth ? 'smooth' : 'auto',
      block: 'center',
    });
  }, []);

  useEffect(() => {
    const smooth = hasScrolledRef.current;
    hasScrolledRef.current = true;
    scrollToMatch(matches[activeIndex], smooth);
  }, [activeIndex, matches, scrollToMatch]);

  const goTo = (index: number) => {
    if (matches.length === 0) return;
    // Wrap around so navigation never dead-ends at either edge.
    setActiveIndex(((index % matches.length) + matches.length) % matches.length);
  };

  const registerRow = (segmentId: string | undefined) => (node: HTMLDivElement | null) => {
    if (!segmentId) return;
    if (node) {
      rowRefs.current.set(segmentId, node);
    } else {
      rowRefs.current.delete(segmentId);
    }
  };

  const speakerLabel = (speaker: string) => {
    if (!isGenericSpeakerName(speaker)) return speaker;
    const num = speaker.match(/\d+/)?.[0];
    const prefix = t('meeting-secretary.action-items.speaker-prefix');
    return num ? `${prefix} ${num}` : speaker;
  };

  const heading = meeting?.title?.trim() || t('meeting-secretary.meetings.transcript-source');

  const metadata = [
    meeting?.scheduledStartTime ? formatMeetingDate(meeting.scheduledStartTime, i18n.language) : '',
    meeting?.scheduledStartTime && meeting.scheduledEndTime
      ? formatMeetingTime(
          {
            scheduledStartTime: meeting.scheduledStartTime,
            scheduledEndTime: meeting.scheduledEndTime,
          },
          i18n.language,
          t('meeting-secretary.meetings.minutes'),
        )
      : '',
    meeting?.location?.trim() ?? '',
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={heading}
      data-state={isClosing ? 'closed' : 'open'}
      className={`${SOURCE_PANEL_CLASS} flex flex-col h-full w-full sm:w-[36rem] max-w-full bg-[var(--ds-color-bg-primary)] shadow-2xl`}
    >
      <div className="flex items-start justify-between gap-4 px-4 py-2 min-h-[var(--ds-space-10)] bg-[var(--ds-color-bg-primary)] text-[var(--ds-color-text-primary)] border-b border-[var(--ds-color-border-primary)]">
        <div className="min-w-0">
          <h2 className="font-semibold text-sm truncate" title={heading}>
            {heading}
          </h2>
          {metadata && (
            <p className="text-xs text-[var(--ds-color-text-secondary)] mt-0.5 truncate">
              {metadata}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {matches.length > 0 && (
            <div className="flex items-center gap-1 rounded-[var(--ds-radius-4)] border border-[var(--ds-color-border-primary)] bg-[var(--ds-color-bg-secondary)] px-2 py-1">
              <span className="text-xs whitespace-nowrap text-[var(--ds-color-text-secondary)]">
                {t('meeting-secretary.meetings.source-segment-position', {
                  current: activeIndex + 1,
                  total: matches.length,
                })}
              </span>
              <Button
                tertiary
                iconOnly
                icon={FaChevronUp}
                iconSize={10}
                className="!p-1 rounded-[var(--ds-radius-4)] !text-[var(--ds-color-text-secondary)] hover:bg-[var(--ds-color-bg-secondary-dark)]"
                onClick={() => goTo(activeIndex - 1)}
                title={t('meeting-secretary.meetings.previous-source-segment')}
                aria-label={t('meeting-secretary.meetings.previous-source-segment')}
              />
              <Button
                tertiary
                iconOnly
                icon={FaChevronDown}
                iconSize={10}
                className="!p-1 rounded-[var(--ds-radius-4)] !text-[var(--ds-color-text-secondary)] hover:bg-[var(--ds-color-bg-secondary-dark)]"
                onClick={() => goTo(activeIndex + 1)}
                title={t('meeting-secretary.meetings.next-source-segment')}
                aria-label={t('meeting-secretary.meetings.next-source-segment')}
              />
            </div>
          )}
          <Button
            tertiary
            iconOnly
            icon={HiOutlineXMark}
            iconSize={22}
            className="!p-1 rounded-[var(--ds-radius-4)] !text-[var(--ds-color-text-secondary)] hover:bg-[var(--ds-color-bg-secondary-dark)]"
            onClick={onClose}
            aria-label={t('meeting-secretary.meetings.cancel', 'Close')}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto bg-[var(--ds-color-bg-secondary)] p-4">
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Spinner size="xsmall" />
            <span className="ml-2 text-sm text-[var(--ds-color-text-secondary)]">
              {t('meeting-secretary.meetings.loading-transcripts')}
            </span>
          </div>
        )}
        {!isLoading && hasError && (
          <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded p-3 flex items-center justify-between gap-3">
            <span>{t('meeting-secretary.meetings.error-loading-transcripts')}</span>
            {onRetry && (
              <Button secondary rounded className="shrink-0 !py-1 !text-xs" onClick={onRetry}>
                {t('meeting-secretary.meetings.try-again')}
              </Button>
            )}
          </div>
        )}
        {!isLoading && !hasError && matches.length === 0 && (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded p-3 mb-4">
            {t('meeting-secretary.meetings.no-source-segments')}
          </div>
        )}
        {!isLoading && !hasError && (
          <div className="bg-[var(--ds-color-bg-primary)] border border-[var(--ds-color-border-primary)] shadow-sm rounded-[var(--ds-radius-4)] px-5 py-6 space-y-3">
            {entries.map((entry, index) => {
              const isHighlighted = !!entry.segmentId && highlightedIds.has(entry.segmentId);
              const isActive = isHighlighted && entry.segmentId === matches[activeIndex];
              return (
                <div
                  key={entry.segmentId ?? `${entry.timestamp}-${index}`}
                  ref={registerRow(entry.segmentId)}
                  data-segment-id={entry.segmentId}
                  data-highlighted={isHighlighted || undefined}
                  className={`rounded px-3 py-2 transition-colors ${
                    isHighlighted
                      ? `bg-amber-100 border-l-4 ${isActive ? 'border-amber-500' : 'border-amber-300'}`
                      : 'border-l-4 border-transparent'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-1">
                    <span className="text-xs font-semibold text-gray-900">
                      {speakerLabel(entry.speaker)}
                    </span>
                    <span className="text-[11px] text-gray-500">{entry.startTime}</span>
                  </div>
                  <p className="text-sm text-gray-800 leading-relaxed">{entry.text}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export const TranscriptSourceModal: FC<TranscriptSourceModalProps> = ({
  isOpen,
  ...viewerProps
}) => {
  const { onClose } = viewerProps;

  const [rendered, setRendered] = useState(isOpen);

  useEffect(() => {
    if (isOpen) {
      setRendered(true);
      return;
    }
    const timer = setTimeout(() => setRendered(false), SOURCE_PANEL_SLIDE_MS);
    return () => clearTimeout(timer);
  }, [isOpen]);

  const lastOpenProps = useRef(viewerProps);
  useEffect(() => {
    if (isOpen) lastOpenProps.current = viewerProps;
  });
  const shownProps = isOpen ? viewerProps : lastOpenProps.current;

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!rendered) return;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [rendered]);

  if (!isOpen && !rendered) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex justify-end bg-black/40"
      // Backdrop-only close: a click inside the dialog has a deeper target.
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      role="presentation"
    >
      <TranscriptSourceViewer {...shownProps} onClose={onClose} isClosing={!isOpen} />
    </div>,
    document.body,
  );
};
